/**
 * Signature Audit Trail Page
 * Displays comprehensive audit trail for all e-signatures
 * Supports filtering, search, and export for compliance and QA
 */
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useESignature } from '../hooks/useESignature';
import { 
  Shield, Search, Filter, Download, RefreshCw, 
  CheckCircle, XCircle, Clock, FileText, User,
  Calendar, ChevronLeft, ChevronRight, BarChart3
} from 'lucide-react';

const SignatureAuditTrailPage = () => {
  const { getAuditTrail, getAuditStats, loading } = useESignature();
  
  const [auditTrail, setAuditTrail] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, skip: 0, hasMore: false });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    documentType: '',
    signerName: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    const loadData = async () => {
      // Load audit trail
      const trailResult = await getAuditTrail({
        limit: 20,
        skip: 0
      });
      if (trailResult.success) {
        setAuditTrail(trailResult.auditTrail);
        setPagination(trailResult.pagination);
      }
      
      // Load stats
      const statsResult = await getAuditStats();
      if (statsResult.success) {
        setStats(statsResult.stats);
      }
    };
    
    loadData();
  }, [getAuditTrail, getAuditStats]);

  const loadAuditTrail = async (newSkip = 0) => {
    const result = await getAuditTrail({
      ...filters,
      limit: pagination.limit,
      skip: newSkip
    });
    
    if (result.success) {
      setAuditTrail(result.auditTrail);
      setPagination(result.pagination);
    }
  };

  const loadStats = async () => {
    const result = await getAuditStats();
    if (result.success) {
      setStats(result.stats);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    loadAuditTrail(0);
  };

  const handleClearFilters = () => {
    setFilters({
      documentType: '',
      signerName: '',
      startDate: '',
      endDate: ''
    });
    setTimeout(() => loadAuditTrail(0), 100);
  };

  const handleNextPage = () => {
    if (pagination.hasMore) {
      loadAuditTrail(pagination.skip + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      loadAuditTrail(Math.max(0, pagination.skip - pagination.limit));
    }
  };

  const handleExportCSV = () => {
    const headers = ['Signature ID', 'Signer Name', 'Title', 'Document Type', 'Document ID', 'Signed At', 'Status', 'Valid'];
    const rows = auditTrail.map(entry => [
      entry.id,
      entry.signerName,
      entry.signerTitle,
      entry.documentType,
      entry.documentId,
      entry.signedAt,
      entry.status,
      entry.isValid ? 'Yes' : 'No'
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `signature-audit-trail-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'admission_consent': 'Admission Consent',
      'discharge_assessment': 'Discharge Assessment',
      'soc_roc': 'SOC/ROC Assessment',
      'nurses_notes': 'Nurses Notes',
      'poc_485': 'Plan of Care (485)',
      'test': 'Test Document'
    };
    return labels[type] || type;
  };

  const getSignerTitleLabel = (title) => {
    const labels = {
      'patient': 'Patient',
      'responsible_person': 'Responsible Person/Guardian',
      'representative': 'Agency Representative',
      'clinician': 'Clinician'
    };
    return labels[title] || title;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-green-600" />
              Signature Audit Trail
            </h1>
            <p className="text-gray-500 mt-1">Track and verify all electronic signatures for compliance</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => { loadAuditTrail(); loadStats(); }}
              disabled={loading}
              data-testid="refresh-audit-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="outline"
              onClick={handleExportCSV}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalSignatures}</p>
                  <p className="text-xs text-gray-500">Total Signatures</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.validSignatures}</p>
                  <p className="text-xs text-gray-500">Valid Signatures</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.last7Days}</p>
                  <p className="text-xs text-gray-500">Last 7 Days</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.last30Days}</p>
                  <p className="text-xs text-gray-500">Last 30 Days</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Breakdown */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-500" />
                By Document Type
              </h3>
              <div className="space-y-2">
                {Object.entries(stats.byDocumentType || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{getDocumentTypeLabel(type)}</span>
                    <span className="font-medium bg-gray-100 px-2 py-0.5 rounded">{count}</span>
                  </div>
                ))}
                {Object.keys(stats.byDocumentType || {}).length === 0 && (
                  <p className="text-gray-400 text-sm">No data available</p>
                )}
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                By Signer Role
              </h3>
              <div className="space-y-2">
                {Object.entries(stats.bySignerTitle || {}).map(([title, count]) => (
                  <div key={title} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{getSignerTitleLabel(title)}</span>
                    <span className="font-medium bg-gray-100 px-2 py-0.5 rounded">{count}</span>
                  </div>
                ))}
                {Object.keys(stats.bySignerTitle || {}).length === 0 && (
                  <p className="text-gray-400 text-sm">No data available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-gray-700 font-medium"
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          
          {showFilters && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm">Document Type</Label>
                <select
                  value={filters.documentType}
                  onChange={(e) => handleFilterChange('documentType', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                  data-testid="filter-document-type"
                >
                  <option value="">All Types</option>
                  <option value="admission_consent">Admission Consent</option>
                  <option value="discharge_assessment">Discharge Assessment</option>
                  <option value="soc_roc">SOC/ROC Assessment</option>
                  <option value="nurses_notes">Nurses Notes</option>
                  <option value="poc_485">Plan of Care (485)</option>
                </select>
              </div>
              <div>
                <Label className="text-sm">Signer Name</Label>
                <Input
                  value={filters.signerName}
                  onChange={(e) => handleFilterChange('signerName', e.target.value)}
                  placeholder="Search by name..."
                  className="mt-1"
                  data-testid="filter-signer-name"
                />
              </div>
              <div>
                <Label className="text-sm">Start Date</Label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="mt-1"
                  data-testid="filter-start-date"
                />
              </div>
              <div>
                <Label className="text-sm">End Date</Label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="mt-1"
                  data-testid="filter-end-date"
                />
              </div>
              <div className="md:col-span-4 flex gap-2">
                <Button onClick={handleApplyFilters} className="bg-[#f39c12] hover:bg-[#e67e22]" data-testid="apply-filters-btn">
                  <Search className="w-4 h-4 mr-2" />
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={handleClearFilters} data-testid="clear-filters-btn">
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Audit Trail Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="audit-trail-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signed At</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verification</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {auditTrail.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      {loading ? 'Loading...' : 'No signatures found'}
                    </td>
                  </tr>
                ) : (
                  auditTrail.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50" data-testid={`audit-row-${entry.id}`}>
                      <td className="px-4 py-3">
                        {entry.isValid ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs">Valid</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600">
                            <XCircle className="w-4 h-4" />
                            <span className="text-xs">Invalid</span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{entry.signerName}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{getSignerTitleLabel(entry.signerTitle)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          {getDocumentTypeLabel(entry.documentType)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{formatDate(entry.signedAt)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-gray-400" title={entry.verificationHash}>
                          {entry.verificationHash || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handlePrevPage}
                  disabled={pagination.skip === 0}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SignatureAuditTrailPage;
